import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react'
import { useLocation, useHistory, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import '../../fonts/fonts.css'
import SideBar from '../SideBar/index'
import { cartAdd } from '../App/actions'
import shoppingBagIcon from '../../images/icon-shopping-bag.png'
import clockIcon from '../../images/icon-clock.png'
import folderIcon from '../../images/icon-folder.png'
import cartIcon from '../../images/icon-cart-white.png'
import heroBg from '../../images/hero-formation-bg.png'
import cardBg from '../../images/card-formation-bg.png'
import arrowIcon from '../../images/icon-arrow.svg'
import AgencyProductAssignments from './AgencyProductAssignments'
import { describeGroupPrices } from '../ProductsLoader/saga'
import {
  Bg, MainLayout, ContentLayout,
  DetailHeader, DetailIconBox, DetailIconImg, DetailTitle, DetailSubtitle,
  BreadcrumbBar, BreadcrumbItem, BreadcrumbSep,
  MainColumns, LeftCol,
  ImgWrapper, MainImg, ThumbStrip, ThumbItem,
  RightColWrapper, RightCard,
  TitleRow, TitleCol, CardDetailIconImg,
  CatLabel, FormTitle, SepLine,
  PriceBlock, BigPrice, TaxNote, GroupPriceNote,
  MetaRow, MetaLabel, MetaValue,
  QtySection, QtyLabel, QtyCtrl, QtyBtn, QtyNum,
  AddCartBtn, SuccessBar, SuccessLeft, SuccessRight, TrustTxt,
  ContentArea, ContentBox, BoxTitle, ProgramBoxTitle, ProgramToggle, ProgramChevron, SideBySide,
  IncludeList, IncludeItem, CheckMark,
  DescBody,
  ProgramGrid, ProgramItem, ProgramNum, ProgramInfo, ProgramTitle, ProgramDesc,
  ProgramMeta, ProgramTag,
  SequenceBlock, SequenceContent, SequenceList, SequenceItem, SequenceHeader, SequenceBar,
  SequenceNumber, SequenceTitle, SequenceDuration, SequenceDesc,
  ModuleGrid, ModuleCardWrapper, ModuleCardPlaceholder, ModuleCard, ModuleCardImage, ModuleCardNumber, ModuleCardImg,
  ModuleCardBody, ModuleCardMeta, ModuleCardTag, ModuleCardDuration,
  ModuleCardTitle, ModuleCardDesc, ModuleCardHint, ModuleCardTypes,
  ModulePopover, ModalHeader, ModalTitle, ModalDetailsGrid, ModalDetailCard,
  DetailNum
} from './styledComponents'

const POPOVER_GAP = 12
const VIEWPORT_MARGIN = 16

// Placed beside the anchor card (right, else left) and kept inside the viewport
const AnchoredPopover = ({ anchor, contentKey, children, ...props }) => {
  const ref = useRef(null)
  const [pos, setPos] = useState(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    let left = anchor.right + POPOVER_GAP
    if (left + width > vw - VIEWPORT_MARGIN) left = anchor.left - POPOVER_GAP - width
    if (left < VIEWPORT_MARGIN) left = Math.max(VIEWPORT_MARGIN, vw - width - VIEWPORT_MARGIN)
    const top = Math.max(VIEWPORT_MARGIN, Math.min(anchor.top, vh - height - VIEWPORT_MARGIN))
    setPos({ top, left })
  }, [anchor, contentKey])

  return (
    <ModulePopover
      ref={ref}
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
      {...props}
    >
      {children}
    </ModulePopover>
  )
}

// Seeded so each module keeps the same random image between renders
const placeholderUrl = (seed) => `https://picsum.photos/seed/${encodeURIComponent(seed)}/600/400`

const ModuleImage = ({ src, alt }) => {
  const [current, setCurrent] = useState(src)
  return <ModuleCardImg src={current} alt={alt} onError={() => setCurrent(cardBg)} />
}

const renderDetail = (mod, color) => (
  <ProgramItem key={mod.id}>
    <ProgramNum color={color}>{mod.id}</ProgramNum>
    <ProgramInfo>
      <ProgramTitle>{mod.title}</ProgramTitle>
      {(mod.type || mod.duration) && (
        <ProgramMeta>
          {mod.type && <ProgramTag color={color}>{mod.type}</ProgramTag>}
          {mod.duration && <ProgramTag>{mod.duration}</ProgramTag>}
        </ProgramMeta>
      )}
      <ProgramDesc>{mod.description}</ProgramDesc>
    </ProgramInfo>
  </ProgramItem>
)

const CATEGORY_COLORS = {
  Eval:        '#6462FC',
  Conduite:    '#3494FA',
  Automatique: '#31D0BE',
  Eco:         '#00C52B',
  Recyclage:   '#7C4DFF',
  Code:        '#3494FA',
  Hardware:    '#00C8F8'
}

const fmt = (n) => n.toFixed(2).replace('.', ',') + ' €'

const parseDescription = (desc) => {
  if (!desc) return { mainText: '', included: [] }
  const parts = desc.split('Inclus dans ce pack :')
  const mainText = parts[0].trim()
  const included = parts[1]
    ? parts[1].split('\n').map(l => l.replace('•', '').trim()).filter(Boolean)
    : []
  return { mainText, included }
}

const TRUST_TEXT = `Paiement 100% sécurisé\nAccès immédiat après commande\nSupport disponible`

const VIDEO_EXTS = ['.mp4', '.mov', '.webm', '.ogg']
const isVideoFile = (filename) => filename && VIDEO_EXTS.some(ext => filename.toLowerCase().endsWith(ext))
const toArray = (value) => {
  if (Array.isArray(value)) return value
  if (value && typeof value.toJS === 'function') {
    const js = value.toJS()
    return Array.isArray(js) ? js : []
  }
  return []
}
const canonicalId = (value) => {
  if (value === undefined || value === null) return ''
  const str = String(value)
  return (str === 'undefined' || str === 'null') ? '' : str
}

const StoreDetailPage = () => {
  const location   = useLocation()
  const history    = useHistory()
  const { id }     = useParams()
  const dispatch   = useDispatch()
  const productsState = useSelector(state => state.getIn(['global', 'products']))
  const isAdminRaw = useSelector(state => state.getIn(['global', 'isAdmin']))
  const agencyProductsState = useSelector(state => state.getIn(['global', 'agencyProducts']))
  const productsLoading = useSelector(state => state.getIn(['global', 'productsLoading']))
  const cartItemsState  = useSelector(state => state.getIn(['global', 'cartItems']))
  const products = useMemo(
    () => toArray(productsState),
    [productsState]
  )
  const agencyProducts = useMemo(
    () => toArray(agencyProductsState),
    [agencyProductsState]
  )
  const cartItems = useMemo(
    () => toArray(cartItemsState),
    [cartItemsState]
  )
  const isAdmin = isAdminRaw === true || isAdminRaw === 'true' || isAdminRaw === 1 || isAdminRaw === '1'
  const isInCart   = (fId) => cartItems.some(i => i.formation.id === fId)
  const addToCart  = (formation) => dispatch(cartAdd(formation))
  const [qty, setQty]           = useState(1)
  const [activeThumb, setActiveThumb] = useState(0)
  const [activeSequenceIndex, setActiveSequenceIndex] = useState(0)
  const [programOpen, setProgramOpen] = useState(false)
  const [openModule, setOpenModule] = useState(null)
  const openTimer = useRef(null)
  const closeTimer = useRef(null)

  const cancelClose = () => clearTimeout(closeTimer.current)
  // Grace delay lets the pointer travel from the card to the popover without closing it
  const scheduleClose = () => {
    clearTimeout(openTimer.current)
    cancelClose()
    closeTimer.current = setTimeout(() => setOpenModule(null), 150)
  }
  const showModule = (mod, el, sequenceNumber, moduleNumber) => {
    cancelClose()
    clearTimeout(openTimer.current)
    setOpenModule({ mod, sequenceNumber, moduleNumber, anchor: el.getBoundingClientRect() })
  }
  const scheduleOpen = (mod, el, sequenceNumber, moduleNumber) => {
    cancelClose()
    clearTimeout(openTimer.current)
    openTimer.current = setTimeout(() => showModule(mod, el, sequenceNumber, moduleNumber), 60)
  }

  useEffect(() => () => {
    clearTimeout(openTimer.current)
    clearTimeout(closeTimer.current)
  }, [])
  useEffect(() => {
    setActiveSequenceIndex(0)
    setProgramOpen(false)
    setOpenModule(null)
  }, [id])

  useEffect(() => {
    if (!openModule) return undefined
    const close = () => setOpenModule(null)
    const onKeyDown = (e) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKeyDown)
    // Capture phase: the page scrolls inside a container, not the window
    window.addEventListener('scroll', close, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', close, true)
    }
  }, [openModule])

  const routeProductId = canonicalId(id)
  const formationFromState = location.state?.formation
  const stateProductId = formationFromState ? canonicalId(formationFromState.id ?? formationFromState._id ?? '') : ''
  // History state survives reloads and can hold an outdated product shape: prefer the store
  const formation = products.find((f) => canonicalId(f.id ?? f._id ?? '') === routeProductId)
    || ((formationFromState && stateProductId === routeProductId) ? formationFromState : undefined)

  // Hooks avant tout early return — dépendances sécurisées si formation est undefined
  // URLs signées par tgd-api, renouvelées à chaque rechargement des produits
  const mediaUrls = useMemo(() => (formation?.images || []).map((img, i) =>
    (img && formation.imageUrls && formation.imageUrls[i]) || heroBg
  ), [formation])

  const iconUrl = formation?.iconUrl || ''

  const moduleImageUrls = useMemo(() => {
    const urls = {}
    ;(formation?.sequences || []).forEach(s => (s.modules || []).forEach(m => {
      if (m.imageUrl) urls[m.id] = m.imageUrl
    }))
    return urls
  }, [formation])

  if (!formation) {
    console.log('[StoreDetailPage] formation not found', {
      routeProductId,
      productsCount: products.length,
      productsLoading,
      hasFormationInLocationState: Boolean(formationFromState),
    })
    return (
      <Bg>
        <SideBar />
        <MainLayout>
          <ContentLayout>
            <DetailHeader>
              <DetailIconBox>
                <DetailIconImg src={shoppingBagIcon} alt='' />
              </DetailIconBox>
              <div>
                <DetailTitle>Magasin</DetailTitle>
                <DetailSubtitle>Trouver la formation qu'il vous faut</DetailSubtitle>
              </div>
            </DetailHeader>
            <ContentArea>
              <ContentBox>
                <BoxTitle>{productsLoading ? 'Chargement du produit...' : 'Produit introuvable'}</BoxTitle>
                <DescBody>
                  {productsLoading
                    ? "Le détail du produit est en cours de chargement."
                    : "Le produit demandé n'existe pas ou n'est plus disponible."
                  }
                </DescBody>
              </ContentBox>
            </ContentArea>
          </ContentLayout>
        </MainLayout>
      </Bg>
    )
  }

  if (!isAdmin) {
    const allowedProductsSet = new Set((agencyProducts || []).map(String))
    const formationId = canonicalId(formation.id ?? formation._id ?? '')
    if (!allowedProductsSet.has(formationId)) {
      console.log('[StoreDetailPage] product forbidden for agency', {
        routeProductId,
        formationId,
        agencyProducts,
      })
      return (
        <Bg>
          <SideBar />
          <MainLayout>
            <ContentLayout>
              <DetailHeader>
                <DetailIconBox>
                  <DetailIconImg src={shoppingBagIcon} alt='' />
                </DetailIconBox>
                <div>
                  <DetailTitle>Magasin</DetailTitle>
                  <DetailSubtitle>Trouver la formation qu'il vous faut</DetailSubtitle>
                </div>
              </DetailHeader>
              <ContentArea>
                <ContentBox>
                  <BoxTitle>Acces non autorise</BoxTitle>
                  <DescBody>Ce produit n'est pas disponible pour votre agence.</DescBody>
                </ContentBox>
              </ContentArea>
            </ContentLayout>
          </MainLayout>
        </Bg>
      )
    }
  }

  const color   = formation.color || CATEGORY_COLORS[formation.category] || '#6462FC'
  const added   = isInCart(formation.id)
  const { mainText, included } = parseDescription(formation.description)
  const sequences = formation.sequences || []
  const selectedSequenceIndex = Math.min(activeSequenceIndex, Math.max(sequences.length - 1, 0))
  const activeSequence = sequences[selectedSequenceIndex]
  const activeModules = activeSequence?.modules || []
  const maxModuleCount = sequences.reduce((max, sequence) => Math.max(max, (sequence.modules || []).length), 0)
  const placeholderCount = Math.max(maxModuleCount - activeModules.length, 0)

  const handleAddToCart = () => {
    if (added) return
    for (let i = 0; i < qty; i++) addToCart(formation)
  }

  return (
    <Bg>
      <SideBar />
      <MainLayout>
        <ContentLayout>
          <DetailHeader>
            <DetailIconBox>
              <DetailIconImg src={shoppingBagIcon} alt='' />
            </DetailIconBox>
            <div>
              <DetailTitle>Magasin</DetailTitle>
              <DetailSubtitle>Trouver la formation qu'il vous faut</DetailSubtitle>
            </div>
          </DetailHeader>

          <BreadcrumbBar>
            <BreadcrumbItem clickable onClick={() => history.push('/store')}>Magasin</BreadcrumbItem>
            <BreadcrumbSep>›</BreadcrumbSep>
            <BreadcrumbItem clickable onClick={() => history.push('/store')}>{formation.category}</BreadcrumbItem>
            <BreadcrumbSep>›</BreadcrumbSep>
            <BreadcrumbItem active>{formation.title}</BreadcrumbItem>
          </BreadcrumbBar>
          <MainColumns>
            <LeftCol>
              <ImgWrapper>
                {(() => {
                  const img = formation.images && formation.images.length > 0
                    ? formation.images[activeThumb] || formation.images[0]
                    : null
                  const url = mediaUrls[activeThumb] || mediaUrls[0] || heroBg
                  if (img && isVideoFile(img)) {
                    return (
                      <video
                        key={formation.id}
                        src={url}
                        controls
                        style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px', background: '#000' }}
                      />
                    )
                  }
                  return (
                    <MainImg
                      src={url}
                      onError={e => { e.target.src = heroBg }}
                    />
                  )
                })()}
              </ImgWrapper>
              <ThumbStrip>
                {(formation.images && formation.images.length > 0 ? formation.images : [null]).map((img, i) => (
                  isVideoFile(img)
                    ? (
                      <ThumbItem
                        as='div'
                        key={i}
                        active={activeThumb === i}
                        onClick={() => setActiveThumb(i)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111', color: '#fff', fontSize: '1.4rem', cursor: 'pointer' }}
                      >
                        ▶
                      </ThumbItem>
                    )
                    : (
                      <ThumbItem
                        key={i}
                        src={mediaUrls[i] || heroBg}
                        active={activeThumb === i}
                        onClick={() => setActiveThumb(i)}
                      />
                    )
                ))}
              </ThumbStrip>
            </LeftCol>

            {/* Right — purchase card */}
            <RightColWrapper>
              <RightCard>
                <TitleRow>
                  <TitleCol>
                    <CatLabel>{formation.category.toUpperCase()}</CatLabel>
                    <FormTitle color={color}>{formation.title}</FormTitle>
                  </TitleCol>
                    <CardDetailIconImg src={iconUrl} alt='' /> 
                </TitleRow>
                <SepLine />

                <PriceBlock>
                  {formation.price != null
                    ? (
                      <>
                        <BigPrice>{fmt(formation.price * qty)}</BigPrice>
                        <TaxNote>TVA incluse · {fmt(formation.price)} / unité</TaxNote>
                      </>
                    )
                    : <TaxNote>Non vendu au tarif standard</TaxNote>}
                  {formation.groupPrices && formation.groupPrices.length > 0 && (
                    <GroupPriceNote>{describeGroupPrices(formation.groupPrices)}</GroupPriceNote>
                  )}
                </PriceBlock>

                <MetaRow>
                  <MetaLabel>
                    <img src={folderIcon} alt='' />
                    Catégorie
                  </MetaLabel>
                  <MetaValue>{formation.category}</MetaValue>
                </MetaRow>
                <MetaRow>
                  <MetaLabel>
                    <img src={clockIcon} alt='' />
                    Durée
                  </MetaLabel>
                  <MetaValue>{formation.duration}</MetaValue>
                </MetaRow>

                <SepLine />

                <QtySection>
                  <QtyLabel>Quantité</QtyLabel>
                  <QtyCtrl>
                    <QtyBtn onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty <= 1}>−</QtyBtn>
                    <QtyNum>{qty}</QtyNum>
                    <QtyBtn onClick={() => setQty(q => q + 1)}>+</QtyBtn>
                  </QtyCtrl>
                </QtySection>

                {!isAdmin && (
                  <>
                    <AddCartBtn onClick={handleAddToCart} disabled={added}>
                      <img src={cartIcon} alt='' />
                      {added ? 'Ajouté au panier' : 'Ajouter au panier'}
                    </AddCartBtn>

                    {added && (
                      <SuccessBar onClick={() => history.push('/cart')}>
                        <SuccessLeft>
                          ✓ Ajouté au panier
                        </SuccessLeft>
                        <SuccessRight>
                          Voir mon panier
                          <img src={arrowIcon} alt='' style={{ width: 40, height: 9, objectFit: 'contain', marginLeft: 15, verticalAlign: 'middle' }} />
                        </SuccessRight>
                      </SuccessBar>
                    )}
                  </>
                )}

                <TrustTxt>{TRUST_TEXT}</TrustTxt>
              </RightCard>
            </RightColWrapper>

          </MainColumns>

          {/* Content sections */}
          <ContentArea>

            {isAdmin && <AgencyProductAssignments formationId={formation.id} />}

            {included.length > 0 && (
              <ContentBox>
                <BoxTitle>Ce qui est inclus</BoxTitle>
                <IncludeList>
                  {included.map((item, i) => (
                    <IncludeItem key={i}>
                      <CheckMark />
                      {item}
                    </IncludeItem>
                  ))}
                </IncludeList>
              </ContentBox>
            )}

            {((formation.content && formation.content.length > 0) || mainText) && (
            <SideBySide>
            {formation.content && formation.content.length > 0 && (
              <ContentBox style={{ flex: 1 }}>
                <BoxTitle>Contenu de la formation</BoxTitle>
                <IncludeList>
                  {formation.content.flatMap((block, i) => {
                    const text = String(block || '').trim()
                    if (!text) return []

                    const lines = text.includes('\n')
                      ? text
                          .split('\n\n')
                          .flatMap(part => part.split('\n'))
                          .filter(l => l.trim())
                      : [text]

                    return lines.map((line, j) => (
                      <IncludeItem key={`${i}-${j}`}>
                        <CheckMark />
                        {line.replace(/^✔️\s*/, '').trim()}
                      </IncludeItem>
                    ))
                  })}
                </IncludeList>
              </ContentBox>
            )}

            {mainText ? (
              <ContentBox style={{ flex: 1.6 }}>
                <BoxTitle>Description</BoxTitle>
                <DescBody>{mainText}</DescBody>
              </ContentBox>
            ) : null}
            </SideBySide>
            )}

            {sequences.length > 0 ? (
              <ContentBox>
                <ProgramToggle
                  type='button'
                  aria-expanded={programOpen}
                  onClick={() => {
                    setOpenModule(null)
                    setProgramOpen(open => !open)
                  }}
                >
                  <ProgramBoxTitle as='span'>Le programme en détail</ProgramBoxTitle>
                  <ProgramChevron $open={programOpen} />
                </ProgramToggle>
                {programOpen && (
                <SequenceBlock>
                  <SequenceContent>
                    <SequenceList>
                    {sequences.map((seq, i) => (
                      <SequenceItem key={i}>
                        <SequenceHeader
                          type='button'
                          active={selectedSequenceIndex === i}
                          aria-pressed={selectedSequenceIndex === i}
                          onClick={() => {
                            clearTimeout(openTimer.current)
                            clearTimeout(closeTimer.current)
                            setOpenModule(null)
                            setActiveSequenceIndex(i)
                          }}
                        >
                          <SequenceBar active={selectedSequenceIndex === i}>
                            <SequenceNumber active={selectedSequenceIndex === i}>{i + 1}</SequenceNumber>
                            <SequenceTitle>{seq.title || `Séquence ${i + 1}`}</SequenceTitle>
                            {seq.duration && (
                              <SequenceDuration>
                                <img src={clockIcon} alt='' />
                                {seq.duration}
                              </SequenceDuration>
                            )}
                          </SequenceBar>
                        </SequenceHeader>
                        {seq.description ? <SequenceDesc>{seq.description}</SequenceDesc> : null}
                      </SequenceItem>
                    ))}
                    </SequenceList>
                    <ModuleGrid key={selectedSequenceIndex}>
                    {activeModules.map((mod, moduleIndex) => (
                      <ModuleCardWrapper
                        key={mod.id}
                        tabIndex={0}
                        role='button'
                        onClick={(e) => showModule(mod, e.currentTarget, selectedSequenceIndex + 1, moduleIndex + 1)}
                        onMouseEnter={(e) => scheduleOpen(mod, e.currentTarget, selectedSequenceIndex + 1, moduleIndex + 1)}
                        onMouseLeave={scheduleClose}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); showModule(mod, e.currentTarget, selectedSequenceIndex + 1, moduleIndex + 1) } }}
                      >
                        <ModuleCard $index={moduleIndex}>
                          <ModuleCardImage>
                            <ModuleCardNumber>
                              <span>MODULE</span>
                              <strong>{selectedSequenceIndex + 1}.{moduleIndex + 1}</strong>
                            </ModuleCardNumber>
                            <ModuleImage
                              src={moduleImageUrls[mod.id] || placeholderUrl(`${formation.id}-${mod.id}`)}
                              alt={mod.title}
                            />
                          </ModuleCardImage>
                          <ModuleCardBody>
                            {mod.duration && (
                              <ModuleCardMeta style={{ justifyContent: 'flex-end' }}>
                                <ModuleCardDuration>
                                  <img src={clockIcon} alt='' style={{ width: 11, height: 11, objectFit: 'contain' }} />
                                  {mod.duration}
                                </ModuleCardDuration>
                              </ModuleCardMeta>
                            )}
                            <ModuleCardTitle color={color}>{mod.title}</ModuleCardTitle>
                            {mod.description ? <ModuleCardDesc>{mod.description}</ModuleCardDesc> : null}
                            {mod.details.some(d => d.type) && (
                              <ModuleCardTypes>
                                {mod.details.filter(d => d.type).map(d => (
                                  <ProgramTag key={d.id} color={color}>{d.type}</ProgramTag>
                                ))}
                              </ModuleCardTypes>
                            )}
                            {mod.details.length > 0 && (
                              <ModuleCardHint color={color}>
                                {mod.details.length} détail{mod.details.length > 1 ? 's' : ''} — survolez pour voir
                              </ModuleCardHint>
                            )}
                          </ModuleCardBody>
                        </ModuleCard>
                      </ModuleCardWrapper>
                    ))}
                    {Array.from({ length: placeholderCount }, (_, index) => (
                      <ModuleCardPlaceholder key={`empty-${index}`} aria-hidden='true' />
                    ))}
                    </ModuleGrid>
                  </SequenceContent>
                  </SequenceBlock>
                )}
              </ContentBox>
            ) : formation.programme && formation.programme.length > 0 && (
              <ContentBox>
                <ProgramToggle
                  type='button'
                  aria-expanded={programOpen}
                  onClick={() => setProgramOpen(open => !open)}
                >
                  <ProgramBoxTitle as='span'>Le programme en détail</ProgramBoxTitle>
                  <ProgramChevron $open={programOpen} />
                </ProgramToggle>
                {programOpen && (
                  <ProgramGrid>
                    {formation.programme.map(mod => renderDetail(mod, color))}
                  </ProgramGrid>
                )}
              </ContentBox>
            )}

          </ContentArea>

          {openModule && (
            <AnchoredPopover
              anchor={openModule.anchor}
              contentKey={openModule.mod.id}
              role='dialog'
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
                <ModalHeader>
                  <ModuleCardMeta>
                    <ModuleCardTag color={color}>Module {openModule.sequenceNumber}.{openModule.moduleNumber}</ModuleCardTag>
                    {openModule.mod.duration && (
                      <ModuleCardDuration>
                        <img src={clockIcon} alt='' style={{ width: 11, height: 11, objectFit: 'contain' }} />
                        {openModule.mod.duration}
                      </ModuleCardDuration>
                    )}
                  </ModuleCardMeta>
                  <ModalTitle>{openModule.mod.title}</ModalTitle>
                  {openModule.mod.description ? <DescBody>{openModule.mod.description}</DescBody> : null}
                </ModalHeader>
                <ModalDetailsGrid>
                  {openModule.mod.details.map((detail, detailIndex) => (
                    <ModalDetailCard key={detail.id}>
                      <DetailNum color={color}>{openModule.sequenceNumber}.{openModule.moduleNumber}.{detailIndex + 1}</DetailNum>
                      <ProgramTitle>{detail.title}</ProgramTitle>
                      {(detail.type || detail.duration) && (
                        <ProgramMeta>
                          {detail.type && <ProgramTag color={color}>{detail.type}</ProgramTag>}
                          {detail.duration && <ProgramTag>{detail.duration}</ProgramTag>}
                        </ProgramMeta>
                      )}
                      {detail.description ? <ProgramDesc>{detail.description}</ProgramDesc> : null}
                    </ModalDetailCard>
                  ))}
                </ModalDetailsGrid>
            </AnchoredPopover>
          )}

        </ContentLayout>
      </MainLayout>
    </Bg>
  )
}

export default StoreDetailPage
